import { useState, useEffect } from "react";
import { Box } from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import { useNavigate } from "react-router";
import { request } from "../../config/request.ts";
import DataTable, { Column } from "../../shared/DataTable/index.tsx";
import PageHeader from "../../shared/PageHeader/index.tsx";
import DeleteConfirmation from "../../shared/DeleteConfirmation/index.tsx";
import ActionIcons from "../../shared/ActionIcons/index.tsx";
import PageWrapper from "../../shared/PageWrapper/index.tsx";

interface Transaction {
  id: number;
  date: string;
  voucherNo: string;
  particulars: string;
  debitAmount: number;
  creditAmount: number;
  type: string;
  ledger: { id: number; ledgerName: string };
  status: boolean | string;
}

export default function EntriesList() {
  const navigate = useNavigate();
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(0);
  const [limit, setLimit] = useState(25);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    getTransactions();
  }, [page, limit, search]);

  const getTransactions = async () => {
    setLoading(true);
    const response = await request.get("/api/getTransactions", {
      search,
      limit,
      offset: page,
      orderColumn: "date",
      orderDirection: "desc",
    });
    setTransactions(response?.data?.data?.data);
    setTotalCount(response.data.total);
    setLoading(false);
  };

  const handlePaginationChange = (newPage: number, newLimit: number) => {
    setPage(newPage);
    setLimit(newLimit);
  };

  const [deleteDialog, setDeleteDialog] = useState({
    open: false,
    id: 0,
    title: null as string | null,
  });

  const formatAmount = (value: number) =>
    value != null ? `₹${Number(value).toLocaleString()}` : "-";

  const columns: Column[] = [
    {
      id: "slNo",
      label: "Sl.No",
      minWidth: 60,
      format: (_value, _row, index) => (
        <Box component="span" sx={{ fontWeight: 600, color: "#64748b" }}>
          {page * limit + index + 1}
        </Box>
      ),
    },
    {
      id: "date",
      label: "Date",
      minWidth: 110,
      format: (value) => value || "-",
    },
    {
      id: "voucherNo",
      label: "Voucher No",
      minWidth: 120,
      format: (value) => value || "-",
    },
    {
      id: "particulars",
      label: "Particulars",
      minWidth: 200,
      format: (value) => value || "-",
    },
    {
      id: "debitAmount",
      label: "Debit",
      minWidth: 110,
      align: "right",
      format: (value) => (
        <Box sx={{ fontWeight: 600, fontSize: "0.8125rem", color: "#dc2626" }}>
          {formatAmount(value)}
        </Box>
      ),
    },
    {
      id: "creditAmount",
      label: "Credit",
      minWidth: 110,
      align: "right",
      format: (value) => (
        <Box sx={{ fontWeight: 600, fontSize: "0.8125rem", color: "#16a34a" }}>
          {formatAmount(value)}
        </Box>
      ),
    },
    {
      id: "type",
      label: "Type",
      minWidth: 100,
      align: "center",
      format: (value) => value || "-",
    },
    {
      id: "actions",
      label: "Actions",
      minWidth: 100,
      align: "right",
      format: (_value, row) => (
        <ActionIcons
          actions={[
            {
              icon: <EditIcon />,
              tooltip: "Edit",
              onClick: () => navigate(`/admin/entries/${row.id}`),
            },
            {
              icon: <DeleteIcon />,
              isDelete: true,
              tooltip: "Delete",
              onClick: () =>
                setDeleteDialog({ open: true, id: row.id, title: row?.voucherNo }),
            },
          ]}
        />
      ),
    },
  ];

  return (
    <PageWrapper loading={loading}>
      <PageHeader
        title="Entries"
        showSearch
        searchPlaceholder="Search entries..."
        onSearchChange={(value) => setSearch(value)}
        defaultSearchValue={search}
        primaryButton={{
          label: "Add Entry",
          icon: <AddIcon />,
          onClick: () => {
            navigate("/admin/entries/create");
          },
        }}
      />

      <DataTable
        columns={columns}
        rows={transactions}
        totalCount={totalCount}
        onPaginationChange={handlePaginationChange}
        page={page}
        limit={limit}
      />
      <DeleteConfirmation
        open={deleteDialog.open}
        title={"Delete Entry - " + deleteDialog?.title}
        message="Are you sure you want to delete this entry? This action cannot be undone."
        apiEndpoint={"transactions/"}
        deleteId={deleteDialog.id}
        onCancel={() => setDeleteDialog({ open: false, id: 0, title: null })}
        refetchApi={() => {
          setPage(0);
          getTransactions();
        }}
      />
    </PageWrapper>
  );
}
